/**
 * Adaptation FEAS n° 6 — onglet AIDE : mode d'emploi, FAQ, glossaire, inventaire des compteurs.
 * Les textes vivent dans docs/aide/*.md (source unique, versionnée) et sont embarqués à la construction.
 */
import { marked } from 'marked';
import { useEffect, useMemo, useState } from 'react';
import compteurs from '../../../../docs/aide/compteurs.md?raw';
import faq from '../../../../docs/aide/faq.md?raw';
import glossaire from '../../../../docs/aide/glossaire.md?raw';
import modeDEmploi from '../../../../docs/aide/mode-d-emploi.md?raw';
import technique from '../../../../docs/aide/technique.md?raw';
import references from '../../../../docs/aide/references.md?raw';
import aPropos from '../../../../docs/aide/a-propos.md?raw';

const DOCUMENTS = [
  { cle: 'mode-d-emploi', libelle: "Mode d'emploi", texte: modeDEmploi },
  { cle: 'faq', libelle: 'Questions fréquentes', texte: faq },
  { cle: 'glossaire', libelle: 'Glossaire', texte: glossaire },
  { cle: 'compteurs', libelle: 'Inventaire des compteurs', texte: compteurs },
  { cle: 'technique', libelle: 'Connecter Playtime (MCP, API, webhooks)', texte: technique },
  { cle: 'references', libelle: 'Références réglementaires', texte: references },
  { cle: 'a-propos', libelle: 'À propos', texte: aPropos },
] as const;
type CleDoc = (typeof DOCUMENTS)[number]['cle'];

export function Aide({ params }: { params: URLSearchParams }): JSX.Element {
  const initial = (params.get('doc') as CleDoc | null) ?? 'mode-d-emploi';
  const [cle, setCle] = useState<CleDoc>(DOCUMENTS.some((d) => d.cle === initial) ? initial : 'mode-d-emploi');
  const [recherche, setRecherche] = useState('');
  const doc = DOCUMENTS.find((d) => d.cle === cle) ?? DOCUMENTS[0];
  // Contenu statique, rédigé par le projet : le rendu HTML ne contient aucune donnée utilisateur.
  // L'aide est générique ; « https://<votre-domaine> » est remplacé par l'adresse publique de CETTE installation
  // (PUBLIC_URL de l'API, sinon l'adresse du navigateur).
  const [adressePublique, setAdressePublique] = useState<string>(window.location.origin);
  useEffect(() => { void fetch('/api/version').then((r) => r.json()).then((v: { publicUrl?: string | null }) => { if (v.publicUrl) setAdressePublique(v.publicUrl.replace(/\/$/, '')); }).catch(() => undefined); }, []);
  const html = useMemo(() => marked.parse(doc.texte.replaceAll('https://<votre-domaine>', adressePublique).replaceAll('<votre-domaine>', adressePublique.replace(/^https?:\/\//, '')), { gfm: true }) as string, [doc, adressePublique]);
  const resultats = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (q.length < 3) return [];
    const r: { doc: CleDoc; libelle: string; extrait: string }[] = [];
    for (const d of DOCUMENTS) for (const ligne of d.texte.split('\n')) {
      const i = ligne.toLowerCase().indexOf(q);
      if (i >= 0) r.push({ doc: d.cle, libelle: d.libelle, extrait: ligne.replace(/[#*|`]/g, '').slice(Math.max(0, i - 60), i + 120) });
      if (r.length >= 30) break;
    }
    return r;
  }, [recherche]);
  return (
    <section className="aide-ecran">
      <div className="onglets larges">
        {DOCUMENTS.map((d) => <button key={d.cle} aria-selected={cle === d.cle} onClick={() => { setCle(d.cle); setRecherche(''); }}>{d.libelle}</button>)}
        <input className="recherche-aide" placeholder="Rechercher dans l'aide…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
      </div>
      {resultats.length > 0 ? (
        <ul className="resultats-aide">{resultats.map((r, i) => <li key={i}><button className="lien" onClick={() => { setCle(r.doc); setRecherche(''); }}>{r.libelle}</button> — {r.extrait}</li>)}</ul>
      ) : (
        <article className="document-aide" dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </section>
  );
}
